import type { ListQuery, RequestDetail, Requirements, Role } from "../../../shared/labels/contracts";

export type Queue = "all" | "sales" | "marketing" | "quality" | "printer" | "completed";
export function queuesForRoles(roles: readonly Role[]): Queue[] {
  if (roles.includes("coordinator")) return ["all", "sales", "marketing", "quality", "printer", "completed"];
  const queues: Queue[] = [];
  if (roles.includes("sales")) queues.push("sales");
  if (roles.includes("marketing")) queues.push("marketing");
  if (roles.includes("quality")) queues.push("quality");
  if (roles.includes("printer")) queues.push("printer", "completed");
  return queues;
}

export function defaultQueueForRoles(roles: readonly Role[]): Queue {
  return queuesForRoles(roles)[0] ?? "all";
}
export type WorkspaceView = "requests" | "import" | "work-order";
export type DetailTab = "work" | "history" | "files" | "notifications" | "source";
export type UiFault = { code: string; message: string; status?: number; fields?: Record<string, string> };
export type LoadState = "idle" | "loading" | "ready" | "error" | "unauthorized";
export type Feedback = { tone: "success" | "warning" | "danger"; message: string };
export type DownloadedFile = { url: string; name: string; fileId: string; preview?: string };
export type RequirementField = keyof Requirements;
export type UploadDraft = { file: File | null; kind: "artwork" | "reference"; changeNote: string };
export type QualityDraft = { decision: "approve" | "reject"; reason: string; correctionField: string; correctionMessage: string };
export type PrintDraft = { quantity: string; declaration: boolean };
export type ImportDraft = {
  file: File | null;
  sheetName: string;
  classification: "all" | "required" | "not_required" | "verify";
  selectedRowId: string | null;
  variantKey: string;
  labelKind: string;
  salesUserId: string;
  dueLocal: string;
  confirmed: boolean;
};

export const initialImportDraft: ImportDraft = {
  file: null, sheetName: "", classification: "all", selectedRowId: null, variantKey: "", labelKind: "",
  salesUserId: "", dueLocal: "", confirmed: false,
};

export const initialQuery: ListQuery = {
  q: "", overdue: "false", sort: "updatedAt", direction: "desc", page: 1, pageSize: 30,
};

export function queryForQueue(queue: Queue, query: ListQuery): ListQuery {
  const base = { ...query };
  delete base.state;
  delete base.department;
  if (queue === "sales") return { ...base, page: 1, department: "sales" };
  if (queue === "marketing") return { ...base, page: 1, state: "marketing" };
  if (queue === "quality") return { ...base, page: 1, state: "quality_review" };
  if (queue === "printer") return { ...base, page: 1, state: "print_ready" };
  if (queue === "completed") return { ...base, page: 1, state: "print_completed" };
  return { ...base, page: 1 };
}

export function rolesInclude(roles: readonly Role[], role: Role): boolean {
  return roles.includes(role);
}

export function canEditSales(roles: readonly Role[], detail: RequestDetail): boolean {
  return roles.includes("sales") && ["sales_pending", "returned_to_sales"].includes(detail.state);
}

export function requirementsDiffer(a: Requirements, b: Requirements): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

export function latestReview(detail: RequestDetail) {
  return detail.reviews.slice().sort((a, b) => b.cycle - a.cycle)[0] ?? null;
}

export function approvedReview(detail: RequestDetail) {
  if (!detail.approvedFileVersionId) return null;
  return detail.reviews.slice().reverse().find((review) => review.decision === "approved" && review.fileVersionId === detail.approvedFileVersionId) ?? null;
}

export function printReadiness(detail: RequestDetail): { ready: boolean; reason: string } {
  if (detail.state !== "print_ready") return { ready: false, reason: "Talep baskıya hazır aşamasında değil." };
  const review = approvedReview(detail);
  if (!review) return { ready: false, reason: "Güncel onay paketi bulunamadı." };
  const file = detail.files.find((item) => item.id === review.fileVersionId);
  const revision = detail.requirementsRevisions.find((item) => item.id === review.requirementsRevisionId);
  if (!file || file.kind !== "artwork" || !revision || file.requirementsRevisionId !== revision.id) return { ready: false, reason: "Onaylı dosya ve gereklilik bağlantısı doğrulanamadı." };
  if (detail.requestedPrintQuantity === null) return { ready: false, reason: "İstenen etiket adedi belirlenmedi." };
  if (revision.requirements.requestedPrintQuantity !== detail.requestedPrintQuantity) return { ready: false, reason: "İstenen adet onaylanan gereklilik paketiyle eşleşmiyor." };
  return { ready: true, reason: "" };
}

export function dateTime(value: string | null): string {
  if (value === null) return "Belirlenmedi";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Tarih doğrulanamadı";
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "short", timeStyle: "short", timeZone: "Europe/Istanbul",
  }).format(date);
}

export function formatNumber(value: number | null): string {
  return value === null ? "Belirlenmedi" : new Intl.NumberFormat("tr-TR").format(value);
}

export function auditDetails(details: Record<string, unknown>): string {
  const sensitiveKeys = new Set(["pin", "otp", "password", "token", "secret", "oneTimeCode", "accessCode"]);
  return JSON.stringify(details, (key: string, value: unknown) => sensitiveKeys.has(key) ? "[gizlendi]" : value, 2);
}
